import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Shield, CreditCard, RefreshCw } from 'lucide-react'
import { checkoutService, type CreditPack } from '@/services/checkout'
import styles from './Checkout.module.css'

export function Checkout() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [pack, setPack] = useState<CreditPack | null>(null)
  const [loadingPack, setLoadingPack] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [checkoutMounted, setCheckoutMounted] = useState(false)
  const checkoutRef = useRef<any>(null)

  const packId = searchParams.get('pack')

  // Phase 1: Fetch pack data
  useEffect(() => {
    if (!packId) {
      navigate('/users/plans')
      return
    }

    let cancelled = false
    const fetchPack = async () => {
      try {
        setLoadingPack(true)
        setError(null)
        const packs = await checkoutService.getPacks()
        const selectedPack = packs.find(p => p.id === packId)
        if (!selectedPack) throw new Error('Credit pack not found')
        if (!cancelled) setPack(selectedPack)
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load credit pack')
      } finally {
        if (!cancelled) setLoadingPack(false)
      }
    }
    fetchPack()
    return () => { cancelled = true }
  }, [packId])

  // Phase 2: Mount Stripe checkout after pack is loaded and DOM container exists
  useEffect(() => {
    if (!pack || checkoutRef.current) return

    let cancelled = false
    const mountCheckout = async () => {
      try {
        const result = await checkoutService.initializeStripeCheckout(
          pack.id,
          '#checkout-container'
        )
        if (!cancelled) {
          checkoutRef.current = result.checkout
          setCheckoutMounted(true)
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to initialize checkout')
        }
      }
    }
    mountCheckout()

    return () => {
      cancelled = true
      if (checkoutRef.current) {
        checkoutRef.current.destroy()
        checkoutRef.current = null
      }
    }
  }, [pack])

  const handleGoBack = () => {
    navigate('/users/plans')
  }

  if (loadingPack) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-theme-primary animate-spin" />
          <span className="text-theme-secondary font-roboto-mono">INITIALIZING CHECKOUT...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg">
        <div className="max-w-md mx-auto text-center rounded-xl p-8 border-2 bg-surface border-theme-danger">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${styles.errorIconBg}`}>
            <CreditCard className="w-8 h-8 text-theme-danger" />
          </div>
          <h2 className="text-xl font-bold mb-3 text-theme-danger font-orbitron">CHECKOUT_ERROR</h2>
          <p className="text-sm mb-5 text-theme-text">{error}</p>
          <button
            onClick={handleGoBack}
            className="w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 border bg-secondary-glow text-theme-secondary border-theme-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Credit Packs
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      {/* Back button */}
      <div className="px-6 pt-6">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-sm transition-colors text-theme-secondary font-roboto-mono"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK TO PLANS
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pack Summary */}
          <div className="rounded-xl p-6 border-2 bg-surface border-theme-secondary">
            <h2 className="text-lg font-bold mb-5 text-theme-primary font-orbitron">ORDER_SUMMARY.DAT</h2>

            {pack && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="rounded-lg p-4 border bg-surface-hover border-theme-border">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg text-theme-secondary">{pack.name}</h3>
                    <span className="text-2xl font-bold text-theme-primary font-orbitron">
                      {checkoutService.formatPrice(pack.cost)}
                    </span>
                  </div>
                  <p className="text-sm mb-4 text-theme-text">{pack.description}</p>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-theme-text">
                      <span>Credits:</span>
                      <span className="font-medium text-theme-secondary">{pack.credits}</span>
                    </div>
                    <div className="flex justify-between text-theme-text">
                      <span>Per credit:</span>
                      <span className="font-medium text-theme-secondary">
                        {checkoutService.formatPrice(pack.cost / pack.credits)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`pt-4 ${styles.totalDivider}`}>
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span className="text-theme-text">Total:</span>
                    <span className="text-theme-primary font-orbitron text-shadow-primary">
                      {checkoutService.formatPrice(pack.cost)}
                    </span>
                  </div>
                </div>

                {/* Security badges */}
                <div className={`pt-4 mt-4 ${styles.badgesDivider}`}>
                  <div className="flex items-center gap-4 text-xs text-theme-text">
                    <div className="flex items-center gap-1">
                      <Shield className="w-3.5 h-3.5" />
                      <span>Secure payment</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>Powered by Stripe</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Stripe Checkout */}
          <div className="rounded-xl p-6 border-2 bg-surface border-theme-secondary">
            <h2 className="text-lg font-bold mb-5 text-theme-primary font-orbitron">PAYMENT DETAILS</h2>

            {!checkoutMounted && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 mr-2 text-theme-primary animate-spin" />
                <span className="text-sm text-theme-text">Loading payment form...</span>
              </div>
            )}

            <div id="checkout-container" className="min-h-[400px]" />
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 rounded-xl p-5 border bg-surface border-theme-border">
          <h3 className="font-semibold mb-2 text-theme-secondary">What happens next?</h3>
          <ul className="space-y-1 text-sm text-theme-text">
            <li>- Credits will be added to your account instantly after payment</li>
            <li>- You'll receive a confirmation email with your receipt</li>
            <li>- Credits never expire — use them whenever you need</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
